import React, { useRef, useEffect } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { motion, useAnimation, useInView } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * SplitContent - A component that splits content into two columns
 * Responsive design that stacks on mobile
 */
const SplitContent = ({
    leftContent,
    rightContent,
    spacing = 'medium',
    animate = true,
    verticalAlign = 'flex-start',
    leftWidth = '50%',
    rightWidth = '50%',
    className,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const ref = useRef(null);
    const controls = useAnimation();
    const isInView = useInView(ref, { threshold: 0.1 });

    useEffect(() => {
        if (animate && isInView) {
            controls.start("visible");
        }
    }, [isInView, controls, animate]);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    // Spacing configuration
    const getSpacing = () => {
        switch (spacing) {
            case 'compact':
                return theme.spacing(2);
            case 'large':
                return theme.spacing(6);
            case 'medium':
            default:
                return theme.spacing(4);
        }
    };

    const gap = getSpacing();

    return (
        <Box
            ref={ref}
            component={animate ? motion.div : 'div'}
            initial={animate ? "hidden" : undefined}
            animate={animate ? controls : undefined}
            variants={animate ? containerVariants : undefined}
            className={className}
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: gap,
                alignItems: verticalAlign,
                width: '100%',
                ...sx
            }}
            {...props}
        >
            {/* Left Content */}
            <Box
                component={animate ? motion.div : 'div'}
                variants={animate ? itemVariants : undefined}
                sx={{
                    width: isMobile ? '100%' : leftWidth,
                    flex: isMobile ? 'none' : `0 0 ${leftWidth}`,
                }}
            >
                {leftContent}
            </Box>

            {/* Right Content */}
            <Box
                component={animate ? motion.div : 'div'}
                variants={animate ? itemVariants : undefined}
                sx={{
                    width: isMobile ? '100%' : rightWidth,
                    flex: isMobile ? 'none' : `0 0 ${rightWidth}`,
                }}
            >
                {rightContent}
            </Box>
        </Box>
    );
};

SplitContent.propTypes = {
    /** Content for the left column */
    leftContent: PropTypes.node.isRequired,
    
    /** Content for the right column */
    rightContent: PropTypes.node.isRequired,
    
    /** Spacing between columns */
    spacing: PropTypes.oneOf(['compact', 'medium', 'large']),
    
    /** Enable animations */
    animate: PropTypes.bool,
    
    /** Vertical alignment of columns */
    verticalAlign: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'stretch']),
    
    /** Width of left column (CSS value) */
    leftWidth: PropTypes.string,
    
    /** Width of right column (CSS value) */
    rightWidth: PropTypes.string,
    
    /** Additional CSS class */
    className: PropTypes.string,
    
    /** Custom styles */
    sx: PropTypes.object
};

export default SplitContent;
